import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { ROUTES } from "../constants/app";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import ErrorBoundary from "../components/errorBoundary/ErrorBoundary";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { CallsPage } from "@/features/calls/pages/CallsPage";
import { CallDetailPage } from "@/features/calls/pages/CallDetailPage";
import { PartnersPage } from "@/features/partners/pages/PartnersPage";
import { IdeasPage } from "@/features/ideas/pages/IdeasPage";
import { Home } from "@/features/home/pages/Home";
import { GoalsLayout } from "@/features/goals/pages/GoalsLayout";
import { GoalsList } from "@/features/goals/pages/GoalsList";
import { MatchingCallsPage } from "@/features/goals/pages/MatchingCallsPage";
import { SettingsPage } from "@/features/settings/pages/SettingsPage";
import { PartnersDetailsPage } from "@/features/partners/pages/PartnersDetailsPage";
import { UserDetailPage } from "@/features/users/pages/UserDetailPage";
import { IdeaDetailPage } from "@/features/ideas/pages/IdeaDetailPage";
import { EditIdeaPage } from "@/features/ideas/pages/EditIdeaPage";
import { Signup } from "@/features/signup/Signup";

import { Activation } from "@/features/activation/Activation";
import { PasswordReset } from "@/features/auth/pages/PasswordReset";
import LinkedInCallback from "@/features/auth/components/LinkedInCallback"; // FIXED: Use custom component
import { AccountSetupPage } from "@/features/accountSetup/pages/AccountSetupPage";
import { ChatLayout } from "@/features/chat/pages/ChatLayout";

const createSafeLazy = (
  importFn: () => Promise<any>,
  fallbackComponent?: React.ComponentType
) => {
  return React.lazy(() =>
    importFn().catch((error) => {
      console.warn("Lazy loading failed, using fallback:", error);
      return {
        default:
          fallbackComponent || (() => <div>Failed to load component</div>),
      };
    })
  );
};

const DefaultFallback: React.FC = () => (
  <div style={{ padding: "2rem", textAlign: "center" }}>
    <h2>Component temporarily unavailable</h2>
    <p>Please refresh the page or try again later.</p>
  </div>
);

const LoadingSpinner: React.FC = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "200px",
      flexDirection: "column",
      gap: "1rem",
    }}
  >
    <ProgressSpinner />
    <span style={{ color: "#6b7280", fontSize: "0.875rem" }}>Loading...</span>
  </div>
);

const SafeRoute: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback = <DefaultFallback /> }) => {
  return (
    <ErrorBoundary fallback={fallback}>
      <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

const AppRouter: React.FC = () => {
  return (
    <ErrorBoundary fallback={<div>Router Error - Please refresh the page</div>}>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* LinkedIn Callback - MUST BE BEFORE CATCH-ALL */}
            <Route
              path="/user/login/linkedin/callback"
              element={
                <SafeRoute fallback={<div>LinkedIn callback error</div>}>
                  <ErrorBoundary fallback={<div>LinkedIn callback error</div>}>
                    <LinkedInCallback />
                  </ErrorBoundary>
                </SafeRoute>
              }
            />

            {/* Public Routes */}
            <Route
              path={ROUTES.LOGIN}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Login page error</div>}>
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.SIGNUP}
              element={
                <SafeRoute fallback={<div>Signup page error</div>}>
                  <ErrorBoundary fallback={<div>Signup page error</div>}>
                    <PublicRoute>
                      <Signup />
                    </PublicRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.RESET_PASSWORD}
              element={
                <SafeRoute fallback={<div>Password reset page error</div>}>
                  <ErrorBoundary
                    fallback={<div>Password reset page error</div>}
                  >
                    <PublicRoute>
                      <PasswordReset />
                    </PublicRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.ACCOUNT_SETUP}
              element={
                <SafeRoute fallback={<div>Account setup page error</div>}>
                  <ErrorBoundary fallback={<div>Account setup page error</div>}>
                    <AccountSetupPage />
                  </ErrorBoundary>
                </SafeRoute>
              }
            />

            {/* Private Routes */}
            <Route
              path={ROUTES.HOME}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Home page error</div>}>
                    <PrivateRoute>
                      <Home />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.CALLS}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Calls page error</div>}>
                    <PrivateRoute>
                      <CallsPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.CALLS_FOR_YOU}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Calls page error</div>}>
                    <PrivateRoute>
                      <CallsPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.MY_CALLS}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Calls page error</div>}>
                    <PrivateRoute>
                      <CallsPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.CALL_DETAIL}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Call detail page error</div>}>
                    <PrivateRoute>
                      <CallDetailPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path="/partners/*"
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Partners page error</div>}>
                    <PrivateRoute>
                      <PartnersPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.PARTNER_DETAILS}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Partners page error</div>}>
                    <PrivateRoute>
                      <PartnersDetailsPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.USER_DETAILS}
              element={
                <SafeRoute fallback={<div>User page error</div>}>
                  <ErrorBoundary fallback={<div>User detail page error</div>}>
                    <PrivateRoute>
                      <UserDetailPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.IDEAS}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Ideas page error</div>}>
                    <PrivateRoute>
                      <IdeasPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.IDEAS_MY_IDEAS}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Ideas page error</div>}>
                    <PrivateRoute>
                      <IdeasPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.IDEAS_DETAIL}
              element={
                <SafeRoute fallback={<div>Register page error</div>}>
                  <ErrorBoundary fallback={<div>Ideas page error</div>}>
                    <PrivateRoute>
                      <IdeaDetailPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.IDEAS_EDIT}
              element={
                <SafeRoute fallback={<div>Edit idea page error</div>}>
                  <ErrorBoundary fallback={<div>Edit idea page error</div>}>
                    <PrivateRoute>
                      <EditIdeaPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.EMAIL_ACTIVATION}
              element={
                <SafeRoute fallback={<div>Email activation page error</div>}>
                  <ErrorBoundary
                    fallback={<div>Email activation page error</div>}
                  >
                    <Activation />
                  </ErrorBoundary>
                </SafeRoute>
              }
            />

            {/* Goals Routes */}
            <Route
              path={ROUTES.GOALS}
              element={
                <SafeRoute fallback={<div>Goals error</div>}>
                  <ErrorBoundary fallback={<div>Goals page error</div>}>
                    <PrivateRoute>
                      <GoalsLayout />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            >
              <Route index element={<GoalsList />} />
              <Route path=":goalId/matches" element={<MatchingCallsPage />} />
            </Route>

            {/* Settings Routes */}
            <Route
              path="/settings/*"
              element={
                <SafeRoute fallback={<div>Settings error</div>}>
                  <ErrorBoundary fallback={<div>Settings page error</div>}>
                    <PrivateRoute>
                      <SettingsPage />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />

            {/* Chat Routes */}
            <Route
              path={ROUTES.CHAT_INBOX}
              element={
                <SafeRoute fallback={<div>Chat error</div>}>
                  <ErrorBoundary fallback={<div>Chat page error</div>}>
                    <PrivateRoute>
                      <ChatLayout />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />
            <Route
              path={ROUTES.CHAT}
              element={
                <SafeRoute fallback={<div>Chat error</div>}>
                  <ErrorBoundary fallback={<div>Chat page error</div>}>
                    <PrivateRoute>
                      <ChatLayout />
                    </PrivateRoute>
                  </ErrorBoundary>
                </SafeRoute>
              }
            />

            {/* CATCH-ALL MUST BE LAST */}
            <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default AppRouter;
